import {
  Box,
  Button,
  Collapse,
  type ColorProps,
  Grid,
  type GridProps,
  HStack,
  IconButton,
  Text,
  Tooltip,
  useBoolean,
  VStack,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import type { TEvaluationComponentProps } from "@src/components/modules/projects/detail/Evaluation/types";
import {
  formatCurrency,
  getSummaryGroupItemValues,
  getSummaryGroupValues,
} from "@src/components/modules/projects/detail/Evaluation/utils";
import {
  type ExpenseStructuredModel,
  OurWorkBudgetItemModel,
  type OurWorkOverviewModel,
  type StatsModel,
} from "@src/components/modules/projects/detail/models";
import { CardHeader } from "@src/components/modules/projects/detail/Stats/CardHeader";
import { Card } from "@src/components/ui-kit";
import { CollapseIcon, Icon } from "@src/components/ui-kit/Icon";
import { percent } from "@src/utils/formatters";
import { observer } from "mobx-react-lite";
import { type FC, Fragment, useState } from "react";
import { OurWorkTooltipLabel } from "../ProgressBar/OurWork";

type TSummaryHeaderProps = {
  stats: StatsModel;
  toggleCollapse: () => void;
};

type TSummaryHeaderItemProps = {
  title: string;
  tooltip: string;
  value: string;
  color?: ColorProps["color"];
};

type TSummaryGroupProps = {
  data: OurWorkOverviewModel | ExpenseStructuredModel;
  isOpen: boolean;
  onOpen: (id: string) => void;
};

type TSummaryGroupItemProps = {
  value: string;
  color?: ColorProps["color"];
};

const TEMPLATE_COLUMNS = "20% repeat(4, 1fr) 20%";

const SummaryGrid: FC<GridProps> = (props) => {
  return <Grid gap="4" templateColumns={TEMPLATE_COLUMNS} p="4" {...props} />;
};

export const SummaryHeaderItem: FC<TSummaryHeaderItemProps> = ({
  title,
  tooltip,
  value,
  color,
}) => {
  return (
    <VStack alignItems="flex-start">
      <HStack spacing="2">
        <Text>{title}</Text>
        <Tooltip label={tooltip}>
          <Box>
            <Icon name="info-circle" color="gray.400" />
          </Box>
        </Tooltip>
      </HStack>
      <Text color={color} fontSize="16" fontWeight="bold">
        {value}
      </Text>
    </VStack>
  );
};

const SummaryHeader: FC<TSummaryHeaderProps> = ({ stats, toggleCollapse }) => {
  const {
    credit,
    gross_income,
    net_profit,
    net_margin_percent,
    ourWork: { efficiency_percent, remaining_budget },
  } = stats;

  return (
    <SummaryGrid borderBottomColor="gray.200" borderBottomWidth="thin">
      <HStack>
        <IconButton
          mr="1"
          ml="-3"
          aria-label={t`toggle sub rows`}
          colorScheme="grey"
          onClick={toggleCollapse}
          variant="ghost"
        >
          <CollapseIcon />
        </IconButton>
        <CardHeader title={t`Project Summary`} />
      </HStack>
      <SummaryHeaderItem
        title={t`Invoiced`}
        tooltip={t`Invoiced amount.`}
        value={formatCurrency(credit)}
      />
      <SummaryHeaderItem
        title={t`Gross income`}
        tooltip={t`It is a summary of Revenue (Work) + Profit (Production, Media) + Extras EBI.`}
        value={formatCurrency(gross_income)}
      />
      <SummaryHeaderItem
        title={t`Net Profit`}
        tooltip={t`It is a summary of Profit (Work, Production, Media) + Extras EBI.`}
        value={formatCurrency(net_profit)}
        color={net_profit > 0 ? "green.500" : "red.500"}
      />
      <SummaryHeaderItem
        title={t`Net Margin`}
        tooltip={t`(EBIT)`}
        value={percent.format(net_margin_percent)}
        color={net_margin_percent > 0 ? "green.500" : "red.500"}
      />
      <Tooltip
        label={
          <OurWorkTooltipLabel
            hideBadges
            stats={stats.ourWork}
            isTemplate={false}
          />
        }
      >
        <Box>
          <SummaryHeaderItem
            title={t`Work Efficiency`}
            tooltip={t`Budget - Tracked time x Hourly rate`}
            value={
              percent.format(efficiency_percent) +
              " (" +
              formatCurrency(remaining_budget) +
              ")"
            }
            color={efficiency_percent < 10000 ? "red.500" : undefined}
          />
        </Box>
      </Tooltip>
    </SummaryGrid>
  );
};

export const SummaryGroupItem: FC<TSummaryGroupItemProps> = ({
  value,
  color,
}) => {
  return (
    <Text color={color} fontSize="md" fontWeight="bold">
      {value}
    </Text>
  );
};

const SummaryGroup: FC<TSummaryGroupProps> = observer(function SummaryGroup({
  data,
  isOpen,
  onOpen,
}) {
  const { credit, name, budgetItems, id } = data;
  const { netProfit, netMargin } = getSummaryGroupValues(data);
  const groupHasItems = budgetItems.length > 0;

  return (
    <VStack
      w="full"
      borderBottomWidth="thin"
      borderBottomColor="gray.200"
      spacing="0"
    >
      <HStack w="full">
        <SummaryGrid
          px="4"
          w="full"
          onClick={() => groupHasItems && onOpen(id)}
          cursor="pointer"
          as="button"
          textAlign="start"
        >
          <HStack pl={!groupHasItems ? 6 : undefined}>
            {groupHasItems && (
              <Icon
                name={`chevron-${isOpen ? "up" : "down"}`}
                color="blue.600"
              />
            )}
            <Text color="blue.600" fontSize="md" fontWeight="bold">
              {name}
            </Text>
          </HStack>
          <SummaryGroupItem value={formatCurrency(credit)} />
          <Box />
          <SummaryGroupItem
            value={formatCurrency(netProfit)}
            color={netProfit < 0 ? "red.500" : undefined}
          />
          <SummaryGroupItem
            value={percent.format(netMargin)}
            color={netMargin < 0 ? "red.500" : undefined}
          />
          <Box />
        </SummaryGrid>
      </HStack>
      {groupHasItems && (
        <Box w="full">
          <Collapse animateOpacity in={isOpen}>
            {budgetItems.map((item) => {
              const { netMargin, netProfit, efficiency, negativeEfficiency } =
                getSummaryGroupItemValues(item);

              return (
                <SummaryGrid key={`${item.id}-${item.title}-${item.item_code}`}>
                  <Text ml="6">{item.title}</Text>
                  <Text>{formatCurrency(item.stats.credit)}</Text>
                  <Box />
                  <Text color={netProfit < 0 ? "red.500" : undefined}>
                    {formatCurrency(netProfit)}
                  </Text>
                  <Text color={netMargin < 0 ? "red.500" : undefined}>
                    {percent.format(netMargin)}
                  </Text>
                  <Tooltip
                    isOpen={!efficiency ? false : undefined}
                    label={
                      item instanceof OurWorkBudgetItemModel && (
                        <OurWorkTooltipLabel
                          hideBadges
                          stats={item.stats}
                          isTemplate={false}
                        />
                      )
                    }
                  >
                    <Text color={negativeEfficiency ? "red.500" : undefined}>
                      {efficiency ?? ""}
                    </Text>
                  </Tooltip>
                </SummaryGrid>
              );
            })}
          </Collapse>
        </Box>
      )}
    </VStack>
  );
});

export const Summary: FC<TEvaluationComponentProps> = ({ project }) => {
  const [isOpen, { toggle }] = useBoolean(true);
  const { stats, expensesStructured, ourWorkOverview } = project;
  const [openGroups, setOpenGroups] = useState<string[]>([]);

  const handleClick = () => {
    toggle();

    if (!isOpen) return;

    setOpenGroups([]);
  };

  const handleOpen = (id: string) => {
    setOpenGroups((prev) => {
      return prev.includes(id)
        ? prev.filter((prevId) => prevId !== id)
        : [...prev, id];
    });
  };

  const handleToggleCollapse = () => {
    setOpenGroups((prev) => {
      return prev.length
        ? []
        : [
            ...ourWorkOverview.map(({ id }) => `OW${id}`),
            ...expensesStructured.map(({ id }) => `EX${id}`),
          ];
    });
  };

  return (
    <Fragment>
      <HStack justifyContent="center" w="full" h="12">
        <Button
          color="gray.500"
          onClick={handleClick}
          rightIcon={<Icon name={`chevron-${isOpen ? "up" : "down"}`} />}
          size="lg"
          variant="unstyled"
        >
          <Trans>View detailed financial report</Trans>
        </Button>
      </HStack>
      <Box w="full">
        <Collapse animateOpacity in={isOpen}>
          <Card mb="8" overflow="scroll" maxW="full">
            <Box w={{ base: "1080px", md: "full" }}>
              <SummaryHeader
                stats={stats}
                toggleCollapse={handleToggleCollapse}
              />
              {ourWorkOverview.map((workItem) => (
                <SummaryGroup
                  data={workItem}
                  key={workItem.id}
                  isOpen={openGroups.includes(`OW${workItem.id}`)}
                  onOpen={(id) => handleOpen(`OW${id}`)}
                />
              ))}
              {expensesStructured.map((expenseItem) => (
                <SummaryGroup
                  data={expenseItem}
                  key={expenseItem.id}
                  isOpen={openGroups.includes(`EX${expenseItem.id}`)}
                  onOpen={(id) => handleOpen(`EX${id}`)}
                />
              ))}
            </Box>
          </Card>
        </Collapse>
      </Box>
    </Fragment>
  );
};
